import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LockClosedIcon } from "@heroicons/react/outline";
import { usePeriodLock } from "../../../../Context";

/**
 * PeriodLockBanner — one quiet line stating the books are closed through a date,
 * with a link to manage it.
 *
 * A closed period is a *status*, not a failure, so this uses the house amber
 * tone (the same scale as BusinessStatusBanner's TONES.warning) rather than red:
 * a user who sees red assumes something broke, when in fact their books are
 * tidy. Not reusing BusinessStatusBanner itself because that derives its tone
 * from getBusinessLifecycle() — a different data source entirely.
 *
 * Renders nothing while the book is open, which is what keeps a never-closed
 * business pixel-identical to before this feature existed.
 *
 * Self-fetches through usePeriodLock, so mounting it on a dozen pages still
 * costs one GET (requests collapse in requestPeriodStatus).
 */
const PeriodLockBanner = ({
  businessID,
  className = "",
  manageTo,
  // See PeriodClosedNotice: the embed funnel has nowhere in-frame to link to.
  showManage = true,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const { closedThrough, closedThroughLabel } = usePeriodLock(businessID, {
    locale: i18n.language,
  });

  if (!closedThrough) return null;

  // v1 and v2 have separate routes to the same panel; default to whichever
  // layout the caller is already in so ~12 call sites pass only businessID.
  const target =
    manageTo ||
    (location.pathname.startsWith("/v2/")
      ? "/v2/accounting/period"
      : "/period-close");

  return (
    <div
      className={`flex items-center gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 transition-opacity duration-150 ${className}`}
    >
      {/* Outline at 1.5 to match the regular-weight text beside it. No
          animation: this appears on every visit, so the attention cost repeats. */}
      <LockClosedIcon
        className="h-5 w-5 flex-shrink-0 text-amber-400"
        strokeWidth={1.5}
        aria-hidden="true"
      />
      <p className="text-sm text-amber-800">
        {t("accounting.period.banner.closedThrough", {
          date: closedThroughLabel,
        })}
      </p>
      {showManage && (
        <Link
          to={target}
          className="ml-auto flex-shrink-0 text-sm font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
        >
          {t("accounting.period.banner.manage")}
        </Link>
      )}
    </div>
  );
};

export default PeriodLockBanner;
